import { RequestOptions } from '../types/httpRequests';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;
const MEMBERSHIP_CACHE_TTL_MS = 5000;
const ACCOUNT_MEMBERSHIP_ROLES = new Set(['admin', 'member', 'viewer']);

export type MembershipSummary = {
  org_path_name?: string | null;
  account_path_name?: string | null;
  user_acc_role?: string | null;
};

export type OrganizationMembershipSummary = {
  org_path_name?: string | null;
  user_org_role?: string | null;
};

export type UserMembershipSummary = {
  memberships: MembershipSummary[];
  organizationMemberships: OrganizationMembershipSummary[];
};

type ApiFetchRequest = (req: RequestOptions) => Promise<{
  status: number;
  body?: any;
  isError?: boolean;
} | null>;

let cachedMembershipSummary: UserMembershipSummary | null = null;
let cachedMembershipSummaryAt = 0;
let pendingMembershipRequest: Promise<UserMembershipSummary | null> | null = null;

export const fetchUserMembershipSummary = async (
  apiFetchRequest: ApiFetchRequest,
): Promise<UserMembershipSummary | null> => {
  if (!backendURL) {
    return null;
  }

  const now = Date.now();
  if (cachedMembershipSummary && now - cachedMembershipSummaryAt < MEMBERSHIP_CACHE_TTL_MS) {
    return cachedMembershipSummary;
  }

  if (pendingMembershipRequest) {
    return pendingMembershipRequest;
  }

  pendingMembershipRequest = (async () => {
    const membershipResponse = await apiFetchRequest({
      endpoint: `${backendURL}/users/membership`,
      method: 'GET',
    });

    if (membershipResponse?.status !== 200 || membershipResponse.isError) {
      return null;
    }

    const body = membershipResponse.body ?? {};
    const summary = {
      memberships: Array.isArray(body.memberships) ? body.memberships : [],
      organizationMemberships: Array.isArray(body.organization_memberships) ? body.organization_memberships : [],
    };

    cachedMembershipSummary = summary;
    cachedMembershipSummaryAt = Date.now();

    return summary;
  })().finally(() => {
    pendingMembershipRequest = null;
  });

  return pendingMembershipRequest;
};

export const getAccountLandingPath = (memberships: MembershipSummary[]): string | null => {
  const accountMembership = memberships.find((membership) => {
    const role = membership?.user_acc_role?.toLowerCase();

    return (
      membership?.org_path_name &&
      membership?.account_path_name &&
      role !== undefined &&
      ACCOUNT_MEMBERSHIP_ROLES.has(role)
    );
  });

  if (!accountMembership?.org_path_name || !accountMembership?.account_path_name) {
    return null;
  }

  return `/o/${encodeURIComponent(accountMembership.org_path_name)}/a/${encodeURIComponent(accountMembership.account_path_name)}`;
};

export const getOrgLandingPath = (
  organizationMemberships: OrganizationMembershipSummary[],
  memberships: MembershipSummary[] = [],
): string | null => {
  const accountLandingPath = getAccountLandingPath(memberships);
  if (accountLandingPath) {
    return accountLandingPath;
  }

  const orgMembership = organizationMemberships.find(
    (membership) => membership?.org_path_name && membership?.user_org_role,
  );
  if (!orgMembership?.org_path_name) {
    return null;
  }

  return `/o/${encodeURIComponent(orgMembership.org_path_name)}`;
};
